/**
 * 建房：玩法 / 座位数 / 盲注 / 起始筹码四项，建完立刻把 6 位房间码顶到屏幕正中。
 *
 * 几个刻意的选择：
 *   - **座位数用分段控件而不是下拉框**：手机上原生 `<select>` 会弹全屏滚轮，
 *     选个「6 人」要「点开 → 滚 → 确定」三次交互，而这里 7 个选项一行就摆得下；
 *   - **表单只有四项**：超时、ante、摊牌策略这些服务端都有默认值，
 *     第一次开房的人不该被十几个开关拦住。想改的人进房后在房间设置里调；
 *     玩法（标准 / 短牌）进房后不能再改，所以必须在这一屏选；
 *   - **建完先停在房间码这一屏**，不自动进牌桌：房主此刻要做的事是把码念/发出去，
 *     自动跳进空牌桌只会让他再找一遍「邀请」在哪。
 *
 * 布局（P2）：整屏是一根弹性竖轴 —— 表单贴顶、`.lobby__gap` 吃掉剩余高度、
 * 主按钮固定在 dock。正常尺寸下**不出现内部滚动条**。
 *
 * 每屏一个主 CTA：所以「返回」在内容区顶部（文字按钮），dock 只留「创建房间」；
 * 房间码那一屏的主 CTA 是「复制房间码」，「进入牌桌」降级成描边按钮。
 *
 * 表单状态在 `useLobbyFlow.form`（不是组件 state）：stage 上改、dock 上提交，
 * 两者是 App 的平行 slot，没有父子关系。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { DeckVariant } from '@poker/shared'
import { IconCopy, IconShare } from '../icons/index.js'
import { formatChips } from '../table/cardFormat.js'
import { useUiStore } from '../store/index.js'
import { appOrigin, copyText } from './env.js'
import { InviteSheet } from './InviteSheet.js'
import { useLobbyFlow, type CreatedRoom } from './lobbyFlow.js'
import { IconAlert, IconBack } from './lobbyIcons.js'
import { groupRoomCode, inviteLink } from './roomCode.js'
import {
  canCreate,
  createFormWarnings,
  DECK_VARIANT_CHOICES,
  matchesBlindPreset,
  ROOM_BLIND_PRESETS,
  ROOM_STACK_PRESETS,
  SEAT_COUNT_CHOICES,
  setBlindPreset,
  setCustomBlinds,
  setMaxSeats,
  setCreateStack,
  setDeckVariant,
  stackInBigBlinds,
  validateCreateForm,
} from './createForm.js'
import { useNicknameStore } from './useAuth.js'
import './lobby.css'

/** 取消「自定义盲注」时回落到的预设。取第一个 —— 它就是默认开房用的那档。 */
const FIRST_BLIND_PRESET = ROOM_BLIND_PRESETS[0] ?? { label: '10 / 20', smallBlind: 10, bigBlind: 20 }

// ============================================================================
// 小件
// ============================================================================

/** 返回上一屏。放在内容区顶部而不是 dock —— dock 是这一屏主 CTA 的专属位置。 */
export function BackRow({ onBack }: { onBack(): void }): ReactNode {
  return (
    <button type="button" className="lobby__back" onClick={onBack}>
      <IconBack size={18} />
      返回
    </button>
  )
}

/**
 * 卡片头：左边标签，右边一个安静的「自定义」开关。
 *
 * ★ 为什么把「自定义」从分段控件里拎出来：它和 10/20、25/50 不是同一类选项
 *   （那些是值，它是模式）；更现实的原因是 375px 宽屏上多这一格就会把整条
 *   分段控件挤成两行，三张卡片叠起来正好把内容顶出视口，逼出一条本不该有的
 *   滚动条（P2）。视觉 32px、命中区靠 .hit-expand 补到 44。
 */
function CardHead({
  label,
  custom,
  onToggle,
}: {
  readonly label: string
  readonly custom?: boolean
  onToggle?(): void
}): ReactNode {
  return (
    <div className="lobby-cardhead">
      <span className="lobby-label">{label}</span>
      {onToggle !== undefined && (
        <button
          type="button"
          className="lobby-link hit-expand"
          aria-pressed={custom === true}
          onClick={onToggle}
        >
          自定义
        </button>
      )}
    </div>
  )
}

function Pill({
  on,
  label,
  text = false,
  onPress,
}: {
  readonly on: boolean
  readonly label: string
  /** 文字标签（不是数字）：用正文字体，数字字体在 club 主题下没有中文字形 */
  readonly text?: boolean
  onPress(): void
}): ReactNode {
  return (
    <button type="button" className={text ? 'lobby-pill lobby-pill--text' : 'lobby-pill'} aria-pressed={on} onClick={onPress}>
      {label}
    </button>
  )
}

/** 只收整数的数字输入。`inputMode="numeric"` 让手机直接弹数字键盘。 */
function NumberField({
  label,
  hideLabel = false,
  value,
  onChange,
}: {
  readonly label: string
  /** 卡片头上已经有同名标签时隐藏可见文字；`aria-label` 照旧，屏幕阅读器不受影响 */
  readonly hideLabel?: boolean
  readonly value: number
  onChange(n: number): void
}): ReactNode {
  return (
    <label style={{ display: 'grid', gap: 'var(--sp-1)', flex: '1 1 0', minWidth: 0 }}>
      {!hideLabel && <span className="lobby-note">{label}</span>}
      <input
        className="lobby-input num"
        value={Number.isFinite(value) ? String(value) : ''}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        aria-label={label}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^\d]/g, '')
          onChange(digits === '' ? 0 : Number.parseInt(digits, 10))
        }}
      />
    </label>
  )
}

// ============================================================================
// 建房表单
// ============================================================================

export interface CreateRoomProps {
  /** 覆盖昵称来源（默认读本地昵称） */
  readonly nickname?: string
  /** 顶部提示条（微信提醒等）。由 `Lobby` 决定要不要给。 */
  readonly banner?: ReactNode
}

export function CreateRoom({ nickname, banner }: CreateRoomProps = {}): ReactNode {
  const form = useLobbyFlow((s) => s.form)
  const setForm = useLobbyFlow((s) => s.setForm)
  const error = useLobbyFlow((s) => s.error)
  const room = useLobbyFlow((s) => s.room)
  const go = useLobbyFlow((s) => s.go)
  const storedNickname = useNicknameStore((s) => s.nickname)
  const name = nickname ?? storedNickname

  // 建成了：这一屏整个换成房间码
  if (room !== null) {
    return (
      <RoomCreatedPanel
        code={room.code}
        roomId={room.roomId}
        hostName={name}
        deckVariant={room.deckVariant ?? 'standard'}
        {...(banner === undefined ? {} : { banner })}
      />
    )
  }

  const errors = validateCreateForm(form)
  const warnings = createFormWarnings(form)

  return (
    <div className="lobby">
      {banner}
      <BackRow onBack={() => go('home')} />

      {/* 标题和首屏那个按钮用同一个词（「创建房间」），不换同义词；
          「建好会给你一个房间码」这种话不写 —— 下一屏就是房间码本身 */}
      {/* 玩法放在标题行右侧，不另起一张卡：实测卡片到 dock 的剩余高度
          360×800 只有约 21px，再加一张卡就会逼出滚动条（P2）。
          两颗按钮不带说明 —— 规则只在房间信息面板里说一次（RoomHeader） */}
      <div className="lobby__hero lobby__hero--row">
        <h1 className="lobby__title">创建房间</h1>
        <div className="lobby-seg lobby-seg--pair" role="group" aria-label="玩法">
          {DECK_VARIANT_CHOICES.map((v) => (
            <Pill
              key={v.value}
              text
              on={form.deckVariant === v.value}
              label={v.label}
              onPress={() => setForm(setDeckVariant(form, v.value))}
            />
          ))}
        </div>
      </div>

      {/* 平板 / 方屏上这三张卡片会摊成两列（见 lobby.css 的 .lobby__cards）：
          691×634 那种横向空间要用起来，而不是把手机档布局竖着拉长（DEVICES.md R2） */}
      <div className="lobby__cards">
      {/* 座位数：2~8 全支持，不是只按 6 人调优。
          --wide：宽屏两列布局里这张卡横跨整行 —— 7 个格子是本屏最宽的控件，
          挤进半列会被压到 44px 以下（实测 691×634 上相邻按钮互相压 1.5px），
          换行又会把表单顶出视口（实测多出 11px 的内部滚动）。 */}
      <section className="lobby-card lobby-card--wide">
        {/* label 叫「座位数」而不是「人数」：换个词就自解释了，
            不用再挂一句「是座位数，不是现在有几个人」去解释它 */}
        <CardHead label="座位数" />
        <div className="lobby-seg lobby-seg--nums">
          {SEAT_COUNT_CHOICES.map((n) => (
            <Pill
              key={n}
              on={form.maxSeats === n}
              label={String(n)}
              onPress={() => setForm(setMaxSeats(form, n))}
            />
          ))}
        </div>
      </section>

      <section className="lobby-card">
        <CardHead
          label="盲注"
          custom={form.customBlinds}
          onToggle={() =>
            setForm(
              form.customBlinds
                ? setBlindPreset(form, FIRST_BLIND_PRESET)
                : setCustomBlinds(form, form.smallBlind, form.bigBlind),
            )
          }
        />
        {!form.customBlinds && (
          <div className="lobby-seg">
            {ROOM_BLIND_PRESETS.map((p) => (
              <Pill
                key={p.label}
                on={matchesBlindPreset(form, p)}
                label={p.label}
                onPress={() => setForm(setBlindPreset(form, p))}
              />
            ))}
          </div>
        )}
        {form.customBlinds && (
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <NumberField
              label="小盲"
              value={form.smallBlind}
              onChange={(n) => setForm(setCustomBlinds(form, n, form.bigBlind))}
            />
            <NumberField
              label="大盲"
              value={form.bigBlind}
              onChange={(n) => setForm(setCustomBlinds(form, form.smallBlind, n))}
            />
          </div>
        )}
      </section>

      <section className="lobby-card">
        <CardHead
          label="起始筹码"
          custom={form.customStack}
          onToggle={() => setForm(setCreateStack(form, form.startingStack, !form.customStack))}
        />
        {!form.customStack && (
          <div className="lobby-seg lobby-seg--four">
            {ROOM_STACK_PRESETS.map((n) => (
              <Pill
                key={n}
                on={form.startingStack === n}
                label={formatChips(n)}
                onPress={() => setForm(setCreateStack(form, n))}
              />
            ))}
          </div>
        )}
        {form.customStack && (
          // 卡片头已经写着「起始筹码」，输入框上不再重复一遍；aria-label 仍然给全
          <NumberField
            label="起始筹码"
            hideLabel
            value={form.startingStack}
            onChange={(n) => setForm(setCreateStack(form, n, true))}
          />
        )}
        <p className="lobby-note">
          约 <span className="num">{stackInBigBlinds(form)}</span> 个大盲
        </p>
      </section>

      </div>

      {errors.length > 0 && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{errors[0]}</span>
        </div>
      )}
      {errors.length === 0 && warnings.length > 0 && <p className="lobby-note">{warnings[0]}</p>}
      {error !== null && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* ★ 这里**不再抢剩余高度**（实测修复）：剩余高度已经由 .lobby__cards
          摊进三张卡片之间。旧版让它 flex:1，440×856 上就在最后一张卡片和
          「建房」按钮之间量出 133px 的空带。--sm 只留一点收口。
          「仅供娱乐」那句常驻在顶栏，这里不再重复一遍占掉一行。 */}
      <div className="lobby__gap lobby__gap--sm" />
    </div>
  )
}

// ============================================================================
// 建成：房间码这一屏
// ============================================================================

export interface RoomCreatedPanelProps {
  readonly code: string
  readonly roomId: string
  readonly hostName: string
  /** 短牌房在房主名后面写明「短牌房间」，让他发码前确认开对了；标准房不写 */
  readonly deckVariant?: DeckVariant
  readonly banner?: ReactNode
  /** 覆盖站点根地址（测试用） */
  readonly origin?: string
}

type CopyKind = 'idle' | 'code' | 'link' | 'failed'

/**
 * 纯展示：建成之后的那一屏。单独导出是为了能脱离 store 直接渲染与测试。
 *
 * ★ 房间码是**绝对主角**：超大字号、等宽数字、字距拉开、可长按选中。
 *   唯一的主 CTA 是「复制房间码」—— 6 个字符能穿过任何渠道（微信文字、口头、
 *   截图），链接不一定（DEVICES.md R3）。所以「复制链接」「更多方式」都是描边按钮。
 */
export function RoomCreatedPanel({
  code,
  roomId,
  hostName,
  deckVariant = 'standard',
  banner,
  origin,
}: RoomCreatedPanelProps): ReactNode {
  const [copied, setCopied] = useState<CopyKind>('idle')
  const [inviteOpen, setInviteOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const base = origin ?? appOrigin()
  const link = roomId === '' ? null : inviteLink(base, roomId, code)

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  const flash = useCallback((text: string, kind: CopyKind): void => {
    void copyText(text).then((ok) => {
      setCopied(ok ? kind : 'failed')
      if (timer.current !== null) clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied('idle'), 2400)
    })
  }, [])

  return (
    <div className="lobby">
      {banner}
      <div className="lobby__hero">
        <h1 className="lobby__title">房间建好了</h1>
        <p className="lobby__sub">把房间码发给朋友</p>
      </div>

      {/* 封顶的头部气口：剩余高度归码卡自己（.code-big__row 是 flex:1），
          而不是在标题下面摊出一条 150px 的死带 */}
      <div className="lobby__gap lobby__gap--head" />

      <section className="code-big__row">
        <span className="code-big__cap">房间码</span>
        {/* 字号大到能直接截图发群；.selectable 让长按可选中（复制 API 挂掉时的退路） */}
        <strong className="code-big selectable">{groupRoomCode(code)}</strong>
        <span className="lobby-note">
          {hostName} 的{deckVariant === 'shortDeck' ? '短牌房间' : '房间'}
        </span>
      </section>

      <div className="invite-actions">
        <button type="button" className="lobby-cta pressable" onClick={() => flash(code, 'code')}>
          复制房间码
        </button>
        {/* 常驻占位的一行反馈：没动作时是空行（不写「朋友怎么进房」，上面已经说过），
            出现时也不会把上面的按钮顶动 */}
        <p className={copied === 'idle' ? 'lobby-dock__say' : 'lobby-dock__say lobby-dock__say--ok'} aria-live="polite">
          {copied === 'code' && '已复制'}
          {copied === 'link' && '链接已复制'}
          {copied === 'failed' && '复制失败，长按房间码可以选中'}
        </p>
        <div className="lobby-row">
          <button
            type="button"
            className="lobby-btn"
            disabled={link === null}
            onClick={() => (link === null ? undefined : flash(link, 'link'))}
          >
            <IconCopy size={18} />
            复制链接
          </button>
          <button type="button" className="lobby-btn" onClick={() => setInviteOpen(true)}>
            <IconShare size={18} />
            更多方式
          </button>
        </div>
      </div>

      {/* 尾部也留一段可伸缩的气口：只在上面留白会把房间码压到屏幕下缘，
          高屏（440×856）上标题与码之间空出半屏，看着像页面没加载完。
          两段按 1 : 0.75 分配剩余高度 —— 码落在视觉中心偏上，正是「绝对主角」该在的位置。 */}
      <div className="lobby__gap lobby__gap--tail" />

      {inviteOpen && <InviteSheet code={code} roomId={roomId} onClose={() => setInviteOpen(false)} />}
    </div>
  )
}

// ============================================================================
// dock 区 CTA
// ============================================================================

export interface CreateRoomDockProps extends CreateRoomProps {
  onBack?(): void
  /** 房主看完码之后去牌桌；默认切 uiStore 的视图 */
  onEnter?(): void
  /** 身份还没就绪时置灰（避免发出去必然被拒的 createRoom） */
  readonly disabled?: boolean
  /**
   * 覆盖「已经建好的房间」（单测用）。
   * 静态渲染下 zustand 读的是 `getInitialState()`，没有这个入口就没法验证
   * 建成之后 dock 上那颗按钮长什么样。
   */
  readonly room?: CreatedRoom | null
}

export function CreateRoomDock({
  nickname,
  onEnter,
  disabled = false,
  room: roomProp,
}: CreateRoomDockProps = {}): ReactNode {
  const form = useLobbyFlow((s) => s.form)
  const pending = useLobbyFlow((s) => s.pending)
  const storeRoom = useLobbyFlow((s) => s.room)
  const room = roomProp === undefined ? storeRoom : roomProp
  const submitCreate = useLobbyFlow((s) => s.submitCreate)
  const enterTable = useLobbyFlow((s) => s.enterTable)
  const setView = useUiStore((s) => s.setView)
  const storedNickname = useNicknameStore((s) => s.nickname)
  const name = nickname ?? storedNickname
  const creating = pending?.kind === 'create'

  // 房间码那一屏的主 CTA 是「复制房间码」（在内容区），所以这里降级成描边按钮
  if (room !== null) {
    return (
      <div className="lobby-dock">
        <button
          type="button"
          className="lobby-btn lobby-btn--wide"
          onClick={() => {
            enterTable()
            if (onEnter === undefined) setView('table')
            else onEnter()
          }}
        >
          进入牌桌
        </button>
      </div>
    )
  }

  // 点不动时必须说明为什么（P5）：置灰按钮 + 下面一行具体原因
  const problems = validateCreateForm(form)
  const blockedText = disabled ? '连不上服务器' : (problems[0] ?? null)

  return (
    <div className="lobby-dock">
      <button
        type="button"
        className="lobby-cta pressable"
        disabled={disabled || creating || !canCreate(form)}
        onClick={() => submitCreate(form, name)}
      >
        {/* 按钮和首屏入口、标题用同一个词；座位数与盲注就在上面的表单里，不在按钮上再念一遍 */}
        {creating ? '创建中…' : '创建房间'}
      </button>
      {blockedText !== null && (
        <p className="lobby-dock__say" role="status">
          {blockedText}
        </p>
      )}
    </div>
  )
}

export default CreateRoom
